import { useAuth0 } from "@auth0/auth0-react";
import {
    Add,
    Assessment,
    ChevronLeft,
    ChevronRight,
    Edit,
    Home,
    Info,
    MapsHomeWork,
    MenuOpen,
    PersonAdd,
    Search,
    VpnKey,
} from "@mui/icons-material";
import {
    Box,
    Drawer,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Paper,
    Tooltip,
    Typography,
} from "@mui/material";
import { useLocation, useNavigate } from "@tanstack/react-router";
import type { Dispatch, ReactElement, ReactNode, SetStateAction } from "react";
import { adminRole } from "@/utils/roles.ts";

const adminMenuItems = [
    {
        id: "all-units",
        label: "All Units & Owners",
        icon: <Home />,
        route: "/admin/units",
    },
    { id: "add-unit", label: "Add New Unit", icon: <PersonAdd />, route: "/admin/units/create" },
    {
        id: "unit-info",
        label: "Unit Information",
        icon: <Info />,
        route: "/admin/units/information",
    },
    {
        id: "find-reservation",
        label: "Find reservation",
        icon: <Search />,
        route: "/admin/reservations",
    },
    { id: "guests-report", label: "Guests Report", icon: <Assessment />, route: "/admin/guests" },
    { id: "gate-code", label: "Gate Code", icon: <VpnKey />, route: "/admin/gateCodes" },
];

const ownerMenuItems = [
    {
        id: "book-reservation",
        label: "Book reservation",
        icon: <Add />,
        route: "/owner/reservation/book",
    },
    {
        id: "find-reservation",
        label: "Find reservation",
        icon: <Search />,
        route: "/owner/reservation",
    },
    {
        id: "my-units",
        label: "My Units",
        icon: <MapsHomeWork />,
        route: "/owner/units/",
    },
    { id: "edit-personal-info", label: "Edit Personal Info", icon: <Edit />, route: "/owner" },
];

interface SidebarProps {
    menuItems: {
        id: string;
        label: string;
        icon: ReactElement;
        route: string;
    }[];
    isCollapsed: boolean;
    onToggleCollapse: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ menuItems, isCollapsed, onToggleCollapse }) => {
    const navigate = useNavigate();
    const { pathname } = useLocation();

    const sidebarContent = (isForDrawer = false) => (
        <>
            <Box
                sx={{
                    background: "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)",
                    p: isCollapsed && !isForDrawer ? 1 : 2,
                    borderBottom: "1px solid #e2e8f0",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: isCollapsed && !isForDrawer ? "center" : "space-between",
                }}
            >
                {(!isCollapsed || isForDrawer) && (
                    <Typography variant="h6" sx={{ fontWeight: 600, color: "#1e293b" }}>
                        Navigation
                    </Typography>
                )}

                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {!isForDrawer && (
                        <Tooltip title={isCollapsed ? "Expand" : "Collapse"} placement="right">
                            <IconButton
                                onClick={onToggleCollapse}
                                sx={{
                                    color: "#64748b",
                                    display: { xs: "none", md: "flex" },
                                }}
                            >
                                {isCollapsed ? <ChevronRight /> : <MenuOpen />}
                            </IconButton>
                        </Tooltip>
                    )}

                    <IconButton
                        onClick={onToggleCollapse}
                        sx={{
                            display: { xs: "flex", md: "none" },
                            color: "#64748b",
                        }}
                    >
                        <ChevronLeft />
                    </IconButton>
                </Box>
            </Box>

            <List sx={{ p: 1 }}>
                {menuItems.map((item) => (
                    <ListItem key={item.id} disablePadding sx={{ mb: 0.5 }}>
                        <Tooltip
                            title={isCollapsed && !isForDrawer ? item.label : ""}
                            placement="right"
                            disableHoverListener={!isCollapsed || isForDrawer}
                        >
                            <ListItemButton
                                selected={pathname === item.route}
                                onClick={() => {
                                    return navigate({ to: item.route });
                                }}
                                sx={{
                                    borderRadius: 1.5,
                                    justifyContent:
                                        isCollapsed && !isForDrawer ? "center" : "flex-start",
                                    px: isCollapsed && !isForDrawer ? 1 : 2,
                                    "&.Mui-selected": {
                                        backgroundColor: "#4a90b8",
                                        color: "white",
                                        "&:hover": {
                                            backgroundColor: "#3a7a9a",
                                        },
                                        "& .MuiListItemIcon-root": {
                                            color: "white",
                                        },
                                    },
                                    "&:hover": {
                                        backgroundColor: "#f1f5f9",
                                    },
                                    transition: "all 0.2s ease-in-out",
                                }}
                            >
                                <ListItemIcon
                                    sx={{
                                        minWidth: isCollapsed && !isForDrawer ? "auto" : 40,
                                        color: "#64748b",
                                        justifyContent: "center",
                                    }}
                                >
                                    {item.icon}
                                </ListItemIcon>
                                {(!isCollapsed || isForDrawer) && (
                                    <ListItemText
                                        primary={item.label}
                                        slotProps={{
                                            primary: {
                                                fontSize: "0.9rem",
                                                fontWeight: pathname === item.route ? 600 : 500,
                                            },
                                        }}
                                    />
                                )}
                            </ListItemButton>
                        </Tooltip>
                    </ListItem>
                ))}
            </List>
        </>
    );

    return (
        <>
            {/* Desktop Sidebar */}
            <Paper
                elevation={3}
                sx={{
                    width: isCollapsed ? 80 : 280,
                    height: "fit-content",
                    borderRadius: 2,
                    overflow: "hidden",
                    display: { xs: "none", md: "block" },
                    transition: "width 0.3s ease-in-out",
                }}
            >
                {sidebarContent(false)}
            </Paper>

            {/* Mobile Drawer */}
            <Drawer
                anchor="left"
                open={isCollapsed}
                onClose={onToggleCollapse}
                sx={{
                    display: { xs: "block", md: "none" },
                    "& .MuiDrawer-paper": {
                        width: 280,
                        boxSizing: "border-box",
                    },
                }}
            >
                {sidebarContent(true)}
            </Drawer>
        </>
    );
};

type NavigationProps = {
    isDrawerOpen: boolean;
    setIsDrawerOpen: Dispatch<SetStateAction<boolean>>;
};
const Navigation = ({ isDrawerOpen, setIsDrawerOpen }: NavigationProps): ReactNode => {
    const { user } = useAuth0();

    if (!user) {
        return <></>;
    }

    const menuItems = user?.globalRoles.includes(adminRole) ? adminMenuItems : ownerMenuItems;

    return (
        <Sidebar
            menuItems={menuItems}
            isCollapsed={isDrawerOpen}
            onToggleCollapse={() => setIsDrawerOpen(!isDrawerOpen)}
        />
    );
};

export default Navigation;
